import { addDays, differenceInDays, eachDayOfInterval, endOfDay, type Interval, max, min, startOfDay } from 'date-fns';
import { z } from 'zod';

export type DateInterval = Interval<Date, Date>;

export function getOverallBoundsOfIntervals(intervals: readonly Interval[], minimumWidthInDays = 1): DateInterval {
  if (!intervals.length) {
    throw new Error('intervals array is empty');
  }

  const days = getAllDaysOfIntervals(intervals);
  const start = min(days);
  const end = endOfDay(max([...days, addDays(start, minimumWidthInDays - 1)]));

  return { start, end };
}

export function getAllDaysOfIntervals(intervals: readonly Interval[]): Date[] {
  const timestamps = intervals.flatMap((item) => eachDayOfInterval(item).map((day) => startOfDay(day).getTime()));
  return timestamps
    .filter((value, index, array) => array.indexOf(value) === index)
    .map((timestamp) => new Date(timestamp));
}

export function getIntervalAlignedToStartAndEndOfDay(interval: DateInterval): DateInterval {
  return {
    start: startOfDay(interval.start),
    end: endOfDay(interval.end),
  };
}

export function mergeConsecutiveIntervals(intervals: readonly DateInterval[]): DateInterval[] {
  const intervalsSorted = structuredClone(intervals).toSorted((a, z) => a.start.getTime() - z.start.getTime());
  const firstInterval = intervalsSorted.shift();
  if (!firstInterval) return [];

  const result: DateInterval[] = [];
  let pendingInterval: DateInterval = firstInterval;

  for (const currentInterval of intervalsSorted) {
    if (differenceInDays(startOfDay(currentInterval.start), startOfDay(pendingInterval.end)) < 2) {
      // Tage sind zusammenhängend: Intervall-Ende nachführen
      pendingInterval.end = currentInterval.end;
    } else {
      // Neues Intervall starten
      result.push(pendingInterval);
      pendingInterval = currentInterval;
    }
  }

  result.push(pendingInterval);

  return result;
}

export function setTimeFromTimeString(date: Date, timeStr: string): Date {
  const timeSplits = timeStr.split(':').map((num) => z.coerce.number().parse(num));

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    timeSplits.at(0) ?? 0,
    timeSplits.at(1) ?? 0,
    timeSplits.at(2) ?? 0,
  );
}
